import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import WidgetPlaceholder from "./WidgetPlaceholder";
import { getDashboardWidgetDataApi } from "src/api/dashboardApi";
import DownloadOptionsMenu, {
  type WidgetDownloadOptions,
} from "./DownloadOptionsMenu";
import "./DenialReasonsWidget.scss";

// ─── Types ────────────────────────────────────────────────────────────────────

type ReasonRow = {
  name: string;
  value: number;
  percentage?: number;
  root_cause?: string;
};

type LimitOption = { value: number | string; label: string };

type MetaFilter = {
  key: string;
  label?: string;
  default?: number | string;
  options?: LimitOption[];
};

type ReasonMetadata = {
  limit?: number;
  total_reasons_returned?: number;
  total_denials?: number;
  filters?: MetaFilter[];
};

type Props = {
  isLoading: boolean;
  data: unknown;
  title?: string;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
  // custom_lines self-fetches (see `canFetch` below), so WidgetRenderer's
  // own query for this widget is disabled and it never sees
  // has_download_options to decide whether its header hamburger button
  // should render. This reports the value back up once known — same
  // pattern as CustomTableWidget/DeviationBarWidget.
  onDownloadOptionsChange?: (hasDownloadOptions: boolean) => void;
};

// ─── Root-cause colors ───────────────────────────────────────────────────────
// Color follows the root cause (the entity), never the row's rank. Known
// causes get fixed hues; "other" folds to a recessive slate; anything new
// draws from the fixed-order pool by first appearance. Palette passes CVD
// separation + 3:1 contrast (validated).

const ROOT_CAUSE_COLORS: Record<string, string> = {
  "coverage criteria issue": "#DC2626",
  "labs incomplete": "#D97706",
  "other denial reason": "#64748B",
};

const ROOT_CAUSE_POOL = ["#2563EB", "#7C3AED", "#0D9488"];

const buildRootCauseColorMap = (rows: ReasonRow[]): Map<string, string> => {
  const map = new Map<string, string>();
  let poolIndex = 0;
  rows.forEach((r) => {
    const cause = (r.root_cause ?? "Other denial reason").trim();
    const key = cause.toLowerCase();
    if (map.has(cause)) return;
    const fixed = ROOT_CAUSE_COLORS[key];
    map.set(
      cause,
      fixed ?? ROOT_CAUSE_POOL[poolIndex++ % ROOT_CAUSE_POOL.length] ?? "#64748B",
    );
  });
  return map;
};

// ─── Data unwrap ──────────────────────────────────────────────────────────────

type UnwrapResult = {
  rows: ReasonRow[];
  metadata: ReasonMetadata;
  // Siblings of .data, not nested under .metadata — same envelope shape as
  // DeviationBarWidget/CustomTableWidget's own unwrap.
  hasDownloadOptions?: boolean;
  downloadOptions?: WidgetDownloadOptions;
};

// Accepts the full API response ({ data: rows, metadata }) or a bare rows
// array (the builder preview strips the envelope).
const unwrap = (raw: unknown): UnwrapResult => {
  if (Array.isArray(raw)) return { rows: raw as ReasonRow[], metadata: {} };
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.data)) {
      return {
        rows: obj.data as ReasonRow[],
        metadata: (obj.metadata ?? {}) as ReasonMetadata,
        hasDownloadOptions: obj.has_download_options as boolean | undefined,
        downloadOptions: obj.download_options as
          | WidgetDownloadOptions
          | undefined,
      };
    }
  }
  return { rows: [], metadata: {} };
};

// Fallback until the response's metadata.filters (backend-driven options)
// arrives; the backend list is the source of truth.
const FALLBACK_LIMIT_OPTIONS: LimitOption[] = [
  { value: 5, label: "Top 5" },
  { value: 10, label: "Top 10" },
  { value: 20, label: "Top 20" },
  { value: "all", label: "All" },
];

const FALLBACK_LIMIT_DEFAULT = 5;

// ─── Component ────────────────────────────────────────────────────────────────

export default function DenialReasonsWidget({
  isLoading,
  data: propData,
  title,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
  onDownloadOptionsChange,
}: Props) {
  // null = user hasn't picked yet — omit top_n so the backend default applies.
  const [limit, setLimit] = useState<number | string | null>(null);

  // Like KpiQuadrantWidget: when mounted on a live dashboard this widget owns
  // its fetch so the Top-N selector can refetch with widget_filters. In the
  // builder preview (prop data only) the selector is hidden.
  const canFetch =
    !!dashboardName &&
    !!widgetTemplateId &&
    !!appliedFilters &&
    Object.keys(appliedFilters).length > 0;

  const { data: queryData, isLoading: isFetching } = useQuery({
    queryKey: ["widget", dashboardName, widgetTemplateId, appliedFilters, limit],
    queryFn: ({ signal }) =>
      getDashboardWidgetDataApi(
        dashboardName!,
        String(widgetTemplateId!),
        {
          filters: appliedFilters,
          widget_filters: limit === null ? undefined : { top_n: limit },
        },
        { signal },
      ),
    enabled: canFetch,
    staleTime: 0,
    gcTime: 0,
  });

  const { rows, metadata, hasDownloadOptions, downloadOptions } = useMemo(
    () => unwrap(canFetch ? queryData : propData),
    [canFetch, queryData, propData],
  );

  useEffect(() => {
    onDownloadOptionsChange?.(!!hasDownloadOptions);
  }, [hasDownloadOptions, onDownloadOptionsChange]);

  const downloadMenu = (
    <DownloadOptionsMenu
      menuId={
        widgetTemplateId !== undefined ? String(widgetTemplateId) : undefined
      }
      title={title}
      dashboardName={dashboardName}
      widgetTemplateId={widgetTemplateId}
      appliedFilters={appliedFilters}
      downloadOptions={hasDownloadOptions ? downloadOptions : undefined}
    />
  );

  // Backend-driven Top-N select (metadata.filters, key "top_n") with a
  // hardcoded fallback so the control renders before the first response.
  const topNFilter = metadata.filters?.find((f) => f.key === "top_n");
  const limitOptions = topNFilter?.options?.length
    ? topNFilter.options
    : FALLBACK_LIMIT_OPTIONS;
  const limitValue = limit ?? topNFilter?.default ?? FALLBACK_LIMIT_DEFAULT;

  const causeColors = useMemo(() => buildRootCauseColorMap(rows), [rows]);
  const maxPct = useMemo(
    () => Math.max(...rows.map((r) => r.percentage ?? 0), 1),
    [rows],
  );

  const totalDenials = metadata.total_denials;
  const shownLabel = canFetch
    ? undefined
    : metadata.limit !== undefined
      ? `Top ${metadata.limit}`
      : undefined;

  if ((canFetch ? isFetching : isLoading) || (!canFetch && !propData)) {
    return <WidgetPlaceholder height={240} />;
  }

  return (
    <div className="dnl-widget">
      {downloadMenu}
      {/* Context row: what share of denials is shown + Top-N control */}
      <div
        className="d-flex align-items-center justify-content-between gap-2 border-bottom"
        style={{ paddingBottom: 5, flexShrink: 0 }}
      >
        <span style={{ fontSize: 11.5, color: "var(--bs-secondary-color)" }}>
          {totalDenials !== undefined
            ? `${totalDenials.toLocaleString()} total denials`
            : `${rows.length} reasons`}
          {rows.length > 0 && totalDenials !== undefined
            ? ` · showing ${rows.length}`
            : ""}
        </span>
        {canFetch ? (
          <select
            className="form-select form-select-sm"
            style={{
              width: "auto",
              fontSize: 11,
              minWidth: 72,
              padding: "1px 24px 1px 6px",
              cursor: "pointer",
            }}
            value={String(limitValue)}
            onChange={(e) => {
              const v = e.target.value;
              setLimit(v === "all" ? v : Number(v));
            }}
            aria-label="How many denial reasons to show"
          >
            {limitOptions.map((o) => (
              <option key={String(o.value)} value={String(o.value)}>
                {o.label}
              </option>
            ))}
          </select>
        ) : shownLabel ? (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              color: "var(--bs-secondary-color)",
              background: "var(--bs-tertiary-bg, #f8fafc)",
              border: "1px solid var(--bs-border-color)",
              borderRadius: 20,
              padding: "1px 8px",
              whiteSpace: "nowrap",
            }}
          >
            {shownLabel}
          </span>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <div
          className="d-flex align-items-center justify-content-center flex-fill"
          style={{ fontSize: 12.5, color: "var(--bs-secondary-color)" }}
        >
          No denial reasons available.
        </div>
      ) : (
        <div className="dnl-widget__rows">
          {rows.map((r, i) => {
            const cause = (r.root_cause ?? "Other denial reason").trim();
            const color = causeColors.get(cause) ?? "#64748B";
            const pct = r.percentage ?? 0;
            return (
              <div
                key={`${r.name}-${i}`}
                className="dnl-row"
                style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }}
              >
                <div className="d-flex align-items-baseline gap-2" style={{ minWidth: 0 }}>
                  <span
                    title={cause}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: color,
                      flexShrink: 0,
                      alignSelf: "center",
                    }}
                  />
                  <span
                    className="text-truncate"
                    style={{ flex: 1, fontSize: 12.5, color: "var(--bs-body-color)" }}
                    title={`${r.name} — ${cause}`}
                  >
                    {r.name}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--bs-secondary-color)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {Number(r.value).toLocaleString()}
                  </span>
                  <span
                    style={{
                      fontSize: 12.5,
                      fontWeight: 700,
                      color,
                      minWidth: 46,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {pct.toFixed(1)}%
                  </span>
                </div>
                <div className="dnl-row__track">
                  <div
                    className="dnl-row__fill"
                    style={{
                      width: `${Math.max((pct / maxPct) * 100, 2)}%`,
                      background: color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Root-cause legend (identity is never color-alone) */}
      {causeColors.size > 1 && (
        <div
          className="d-flex flex-wrap border-top"
          style={{ gap: "2px 14px", paddingTop: 5, flexShrink: 0 }}
        >
          {Array.from(causeColors.entries()).map(([cause, color]) => (
            <span key={cause} className="d-inline-flex align-items-center gap-1">
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: color,
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: 10.5, color: "var(--bs-secondary-color)" }}>
                {cause}
              </span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
